// Claims tab of the order detail modal (admin Orders page).
//
// The operator picks the shipped parcel(s) that went missing or arrived
// damaged, then the item(s) inside them. Ticking a parcel ticks all of its
// contents (the usual case is "this whole box"), with Select all / Unselect
// all per parcel for the partial cases. Each line carries a quantity (capped
// at what packing recorded in that parcel) and a claim value, defaulted to
// the order line's unit price × quantity and editable.
//
// The carrier comes from the selected parcels' labels. Only carriers with a
// claims sheet wired up in the BE can take a claim here; anything else gets
// a clear "set this carrier up / claim manually for now" instead of a
// silent nothing. Raised claims sit in the DB until the carrier's scheduled
// submission (Settings > Integrations > Carriers) emails them as one sheet.
//
// Carriers also want the supplier's invoice behind each claimed unit. For
// Amazon-supplied SKUs (BargainFox ASIN/1) the BE builds one from the Amazon
// Liquidation Program invoice data — one PDF, a page per invoice number —
// which downloads straight after a claim is raised and on demand from the
// claims table. Nothing is stored; it is rendered on each download.
import { useEffect, useMemo, useState } from "react";
import { FileText, Minus, Plus, ShieldAlert, X } from "lucide-react";
import { toast } from "react-toastify";
import { apiFetch } from "../../api/apiFetch";
import { useReadOnly } from "../../access/ReadOnly";
import type { DispatchOrder, Parcel } from "../../types/Dispatch";

type T = (k: string, p?: Record<string, string | number>) => string;

interface ClaimsSupport {
    carriers: { carrier_code: string; display_name: string; supported: boolean; enabled: boolean }[];
    claim_types: string[];
}

export interface OrderClaim {
    claim_id: string;
    carrier_code: string;
    tracking_number: string;
    sku: string;
    quantity: number;
    claim_type: string;
    cost: string | number;
    // 'invoice' when the supplier (Amazon Liquidation Program) invoice priced
    // the claim — the figure the carrier gets; 'portal' when no invoice
    // existed and the entered value stood.
    cost_source?: "portal" | "invoice";
    invoice_number?: string | null;
    notes: string | null;
    status: "pending" | "submitted";
    created_by_name: string | null;
    created_at: string;
    submitted_at: string | null;
}

type LineSel = { quantity: number; cost: string; costTouched: boolean };

const CLAIM_TYPES = ["LOSS", "PART-LOSS", "DAMAGE"] as const;
const TYPE_KEY: Record<string, string> = { LOSS: "claims_type_loss", "PART-LOSS": "claims_type_part_loss", DAMAGE: "claims_type_damage" };

const lineKey = (tracking: string, sku: string) => `${tracking} ${sku}`;
const carrierKey = (raw: string | undefined | null) => String(raw ?? "").trim().toLowerCase();
const money = (n: number) => `£${n.toFixed(2)}`;
const formatTs = (iso: string | null): string => {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const OrderClaims = ({ order, t }: { order: DispatchOrder; t: T }) => {
    const readOnly = useReadOnly();
    const parcels: Parcel[] = useMemo(() => order.parcels ?? [], [order.parcels]);
    const [support, setSupport] = useState<ClaimsSupport | null>(null);
    const [existing, setExisting] = useState<OrderClaim[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedParcels, setSelectedParcels] = useState<Set<string>>(new Set());
    const [lines, setLines] = useState<Map<string, LineSel>>(new Map());
    const [claimType, setClaimType] = useState<string>("LOSS");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [cancelling, setCancelling] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);

    // Cancel a claim that has not gone to the carrier yet. The BE refuses
    // (409) once it has been submitted; the list is reloaded either way so
    // a stale "pending" row corrects itself.
    const cancelClaim = async (c: OrderClaim) => {
        if (readOnly || cancelling) return;
        if (!window.confirm(t("claims_cancel_confirm", { sku: c.sku }))) return;
        setCancelling(c.claim_id);
        try {
            const r = await apiFetch(
                `/orders/${encodeURIComponent(order.order_id)}/claims/${encodeURIComponent(c.claim_id)}`,
                { method: "DELETE" },
            );
            if (!r.ok) {
                const data = await r.json().catch(() => ({} as Record<string, unknown>));
                toast.error((data?.message as string) || t("server_error"), { toastId: "claims-cancel-err" });
                await loadExisting();
                return;
            }
            setExisting(prev => prev.filter(x => x.claim_id !== c.claim_id));
            toast.success(t("claims_cancelled"), { toastId: "claims-cancelled" });
        } catch {
            toast.error(t("server_error"), { toastId: "claims-cancel-err" });
        } finally {
            setCancelling(null);
        }
    };

    // The supplier invoices PDF for some claims (just raised) or, with no
    // ids, every claim on the order. A 409 means no claimed SKU had an
    // invoice to show (not Amazon-supplied, or never invoiced) — the BE's
    // message names the SKUs.
    const downloadInvoices = async (claimIds?: string[]) => {
        if (downloading) return;
        setDownloading(true);
        try {
            const qs = claimIds && claimIds.length > 0 ? `?claim_ids=${encodeURIComponent(claimIds.join(","))}` : "";
            const r = await apiFetch(`/orders/${encodeURIComponent(order.order_id)}/claims/invoices.pdf${qs}`, {});
            if (!r.ok) {
                const data = await r.json().catch(() => ({} as Record<string, unknown>));
                toast.error((data?.message as string) || t("claims_invoices_failed"), { toastId: "claims-invoices-err" });
                return;
            }
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `Supplier-invoices-${order.order_number || order.order_id}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch {
            toast.error(t("claims_invoices_failed"), { toastId: "claims-invoices-err" });
        } finally {
            setDownloading(false);
        }
    };

    const loadExisting = async (alive?: () => boolean) => {
        try {
            const r = await apiFetch(`/orders/${encodeURIComponent(order.order_id)}/claims`, {});
            if (!r.ok || (alive && !alive())) return;
            const data: { claims: OrderClaim[] } = await r.json();
            if (alive && !alive()) return;
            setExisting(Array.isArray(data.claims) ? data.claims : []);
        } catch { /* shown as empty */ }
    };

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setSelectedParcels(new Set());
        setLines(new Map());
        setNotes("");
        (async () => {
            try {
                const r = await apiFetch("/orders/claims/support", {});
                if (r.ok && mounted) setSupport(await r.json());
            } catch { /* support stays unknown; submit still validated by the BE */ }
            await loadExisting(() => mounted);
            if (mounted) setLoading(false);
        })();
        return () => { mounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order.order_id]);

    // Starting claim value shown in the box. The BE replaces it with the
    // supplier invoice price when the SKU has one (the carrier's sheet must
    // match the invoice), so this is only what the carrier gets for a SKU
    // without an invoice.
    // Default claim value: the product's COST (products.cost_price, from
    // the Pricing & customs section) × quantity — what we lose, not what
    // the customer paid. A product with no cost basis defaults to 0 and
    // the operator types the value in.
    type ParcelItem = Parcel["items"][number];
    const defaultCost = (it: ParcelItem, qty: number) => ((Number(it.cost_price) || 0) * qty).toFixed(2);

    const linesFor = (p: Parcel, into: Map<string, LineSel>) => {
        for (const it of p.items ?? []) {
            into.set(lineKey(p.tracking_number, it.sku), {
                quantity: it.quantity, cost: defaultCost(it, it.quantity), costTouched: false,
            });
        }
    };

    const setParcel = (p: Parcel, on: boolean) => {
        setSelectedParcels(prev => {
            const next = new Set(prev);
            if (on) next.add(p.tracking_number); else next.delete(p.tracking_number);
            return next;
        });
        setLines(prev => {
            const next = new Map(prev);
            for (const it of p.items ?? []) next.delete(lineKey(p.tracking_number, it.sku));
            if (on) linesFor(p, next);
            return next;
        });
    };

    const setAllParcels = (on: boolean) => {
        setSelectedParcels(on ? new Set(parcels.map(p => p.tracking_number)) : new Set());
        setLines(() => {
            const next = new Map<string, LineSel>();
            if (on) for (const p of parcels) linesFor(p, next);
            return next;
        });
    };

    const setAllItems = (p: Parcel, on: boolean) => {
        setLines(prev => {
            const next = new Map(prev);
            for (const it of p.items ?? []) next.delete(lineKey(p.tracking_number, it.sku));
            if (on) linesFor(p, next);
            return next;
        });
    };

    const toggleItem = (p: Parcel, it: Parcel["items"][number]) => {
        const key = lineKey(p.tracking_number, it.sku);
        setLines(prev => {
            const next = new Map(prev);
            if (next.has(key)) next.delete(key);
            else next.set(key, { quantity: it.quantity, cost: defaultCost(it, it.quantity), costTouched: false });
            return next;
        });
    };

    const stepQty = (p: Parcel, it: Parcel["items"][number], delta: number) => {
        const key = lineKey(p.tracking_number, it.sku);
        setLines(prev => {
            const cur = prev.get(key);
            if (!cur) return prev;
            const quantity = Math.min(it.quantity, Math.max(1, cur.quantity + delta));
            const next = new Map(prev);
            next.set(key, { ...cur, quantity, cost: cur.costTouched ? cur.cost : defaultCost(it, quantity) });
            return next;
        });
    };

    const setCost = (key: string, cost: string) => {
        setLines(prev => {
            const cur = prev.get(key);
            if (!cur) return prev;
            const next = new Map(prev);
            next.set(key, { ...cur, cost, costTouched: true });
            return next;
        });
    };

    // Carrier facts for what is selected.
    const carrierInfo = (code: string) => support?.carriers.find(c => c.carrier_code === code);
    const carrierName = (raw: string | undefined) => carrierInfo(carrierKey(raw))?.display_name ?? (raw ? String(raw).toUpperCase() : "—");
    const selectedCodes = useMemo(() => {
        const s = new Set<string>();
        for (const p of parcels) if (selectedParcels.has(p.tracking_number)) s.add(carrierKey(p.carrier));
        return [...s];
    }, [parcels, selectedParcels]);
    const mixed = selectedCodes.length > 1;
    const unsupported = support ? selectedCodes.filter(c => !(carrierInfo(c)?.supported)) : [];
    const disabled = support ? selectedCodes.filter(c => carrierInfo(c)?.supported && !carrierInfo(c)?.enabled) : [];

    const lineCount = lines.size;
    const total = useMemo(() => [...lines.values()].reduce((s, l) => s + (Number(l.cost) || 0), 0), [lines]);
    const canSubmit = lineCount > 0 && !mixed && unsupported.length === 0 && disabled.length === 0 && !submitting && !readOnly;

    const submit = async () => {
        if (readOnly || !canSubmit) return;
        setSubmitting(true);
        try {
            const body = {
                claim_type: claimType,
                notes: notes.trim() || null,
                parcels: parcels
                    .filter(p => selectedParcels.has(p.tracking_number))
                    .map(p => ({
                        tracking_number: p.tracking_number,
                        items: (p.items ?? [])
                            .filter(it => lines.has(lineKey(p.tracking_number, it.sku)))
                            .map(it => {
                                const l = lines.get(lineKey(p.tracking_number, it.sku))!;
                                return { sku: it.sku, quantity: l.quantity, cost: Number(l.cost) || 0 };
                            }),
                    }))
                    .filter(p => p.items.length > 0),
            };
            const r = await apiFetch(`/orders/${encodeURIComponent(order.order_id)}/claims`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const data = await r.json().catch(() => ({} as Record<string, unknown>));
            if (!r.ok) {
                toast.error((data?.message as string) || t("claims_failed"), { toastId: "claims-submit-err" });
                return;
            }
            toast.success(t("claims_submitted"));
            setSelectedParcels(new Set());
            setLines(new Map());
            setNotes("");
            await loadExisting();
            // The carrier wants the supplier invoices with the claim: hand
            // them over for the lines just raised.
            const created: OrderClaim[] = Array.isArray(data?.claims) ? (data.claims as OrderClaim[]) : [];
            if (created.length > 0) void downloadInvoices(created.map(c => c.claim_id));
        } catch {
            toast.error(t("claims_failed"), { toastId: "claims-submit-err" });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="order-claims">
            <p className="dispatch-replacement-intro">{t("claims_intro")}</p>

            {parcels.length === 0 ? (
                <p className="dispatch-replacement-error">{t("claims_no_parcels")}</p>
            ) : (
                <>
                    <div className="dispatch-replacement-list-head">
                        <span>{t("claims_parcels_heading")}</span>
                        <span className="dispatch-replacement-bulk">
                            <button type="button" className="dispatch-replacement-add-all" disabled={submitting}
                                onClick={() => setAllParcels(true)}>
                                {t("claims_select_all_parcels")}
                            </button>
                            <button type="button" className="dispatch-replacement-add-all"
                                disabled={submitting || selectedParcels.size === 0}
                                onClick={() => setAllParcels(false)}>
                                {t("claims_unselect_all_parcels")}
                            </button>
                        </span>
                    </div>

                    <ul className="order-claims-parcels">
                        {parcels.map((p, idx) => {
                            const selected = selectedParcels.has(p.tracking_number);
                            const items = p.items ?? [];
                            const pickedHere = items.filter(it => lines.has(lineKey(p.tracking_number, it.sku))).length;
                            return (
                                <li key={p.tracking_number} className={`order-claims-parcel${selected ? " order-claims-parcel--selected" : ""}`}>
                                    <label className="order-claims-parcel-head">
                                        <input
                                            type="checkbox"
                                            checked={selected}
                                            disabled={submitting}
                                            onChange={e => setParcel(p, e.target.checked)}
                                            aria-label={`${t("parcel")} ${p.box_position ?? (idx + 1)}`}
                                        />
                                        <span className="order-claims-parcel-title">
                                            {t("parcel")} {p.box_position ?? (idx + 1)}/{parcels.length}
                                        </span>
                                        <span className="dispatch-replacement-tracking">{p.tracking_number}</span>
                                        <span className="order-claims-carrier">{carrierName(p.carrier)}</span>
                                        {selected && (
                                            <span className="order-claims-parcel-count">
                                                {t("claims_items_selected", { n: String(pickedHere), of: String(items.length) })}
                                            </span>
                                        )}
                                    </label>

                                    {selected && (
                                        <div className="order-claims-items">
                                            {items.length === 0 ? (
                                                <p className="dispatch-replacement-error">{t("replacement_no_parcel_lines")}</p>
                                            ) : (
                                                <>
                                                    <div className="dispatch-replacement-list-head order-claims-items-head">
                                                        <span>{t("items")}</span>
                                                        <span className="dispatch-replacement-bulk">
                                                            <button type="button" className="dispatch-replacement-add-all"
                                                                disabled={submitting || pickedHere === items.length}
                                                                onClick={() => setAllItems(p, true)}>
                                                                {t("claims_select_all_items")}
                                                            </button>
                                                            <button type="button" className="dispatch-replacement-add-all"
                                                                disabled={submitting || pickedHere === 0}
                                                                onClick={() => setAllItems(p, false)}>
                                                                {t("claims_unselect_all_items")}
                                                            </button>
                                                        </span>
                                                    </div>
                                                    <ul className="dispatch-replacement-lines">
                                                        {items.map(it => {
                                                            const key = lineKey(p.tracking_number, it.sku);
                                                            const sel = lines.get(key);
                                                            return (
                                                                <li key={key} className="dispatch-replacement-line order-claims-line">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={Boolean(sel)}
                                                                        disabled={submitting}
                                                                        onChange={() => toggleItem(p, it)}
                                                                        aria-label={it.title || it.sku}
                                                                    />
                                                                    <span className="dispatch-replacement-info">
                                                                        <span className="dispatch-replacement-title">{it.title || it.sku}</span>
                                                                        <span className="dispatch-replacement-sku">{it.sku}</span>
                                                                        <span className="dispatch-replacement-stock">
                                                                            {t("claims_packed_in_parcel", { n: String(it.quantity) })}
                                                                        </span>
                                                                    </span>
                                                                    <span className="dispatch-replacement-stepper">
                                                                        <button type="button" aria-label="-1"
                                                                            disabled={submitting || !sel || sel.quantity <= 1}
                                                                            onClick={() => stepQty(p, it, -1)}>
                                                                            <Minus size={16} />
                                                                        </button>
                                                                        <span className="dispatch-replacement-qty">{sel?.quantity ?? 0}</span>
                                                                        <button type="button" aria-label="+1"
                                                                            disabled={submitting || !sel || sel.quantity >= it.quantity}
                                                                            onClick={() => stepQty(p, it, 1)}>
                                                                            <Plus size={16} />
                                                                        </button>
                                                                    </span>
                                                                    <label className="order-claims-cost">
                                                                        <span>£</span>
                                                                        <input
                                                                            type="number"
                                                                            min={0}
                                                                            step="0.01"
                                                                            inputMode="decimal"
                                                                            value={sel?.cost ?? ""}
                                                                            disabled={submitting || !sel}
                                                                            aria-label={t("claims_cost_label")}
                                                                            onChange={e => setCost(key, e.target.value)}
                                                                        />
                                                                    </label>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>

                    {mixed && <p className="dispatch-replacement-error">{t("claims_carrier_mixed")}</p>}
                    {unsupported.map(c => (
                        <p key={c} className="dispatch-replacement-error">
                            {t("claims_carrier_not_supported", { carrier: carrierName(c) })}
                        </p>
                    ))}
                    {disabled.map(c => (
                        <p key={c} className="dispatch-replacement-error">
                            {t("claims_carrier_disabled", { carrier: carrierName(c) })}
                        </p>
                    ))}

                    <div className="order-claims-form">
                        <div className="order-claims-form-row">
                            <span className="dispatch-edit-label order-claims-form-label">{t("claims_type_label")}</span>
                            <div className="order-claims-types" role="radiogroup" aria-label={t("claims_type_label")}>
                                {CLAIM_TYPES.map(ct => (
                                    <label key={ct} className={`order-claims-type${claimType === ct ? " order-claims-type--active" : ""}`}>
                                        <input type="radio" name="claim-type" value={ct} checked={claimType === ct}
                                            disabled={submitting} onChange={() => setClaimType(ct)} />
                                        {t(TYPE_KEY[ct])}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <label className="dispatch-edit-label" htmlFor={`order-claims-notes-${order.order_id}`}>
                            {t("claims_notes_label")}
                        </label>
                        <textarea
                            id={`order-claims-notes-${order.order_id}`}
                            className="dispatch-note-input"
                            rows={2}
                            maxLength={2000}
                            value={notes}
                            disabled={submitting}
                            placeholder={t("claims_notes_placeholder")}
                            onChange={e => setNotes(e.target.value)}
                        />
                        <div className="order-claims-footer">
                            <span className="order-claims-total">
                                {t("claims_total")}: <strong>{money(total)}</strong>
                            </span>
                            <button
                                type="button"
                                className="btn btn-success dispatch-modal-btn"
                                disabled={!canSubmit}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                                onClick={() => void submit()}
                            >
                                <ShieldAlert size={14} />
                                {submitting ? t("saving") : t("claims_submit", { n: String(lineCount) })}
                            </button>
                        </div>
                    </div>
                </>
            )}

            <div className="order-claims-existing-head">
                <h3 className="order-claims-existing-heading">{t("claims_existing_heading")}</h3>
                {existing.length > 0 && (
                    <button
                        type="button"
                        className="order-claims-invoices"
                        disabled={downloading}
                        onClick={() => void downloadInvoices()}
                    >
                        <FileText size={12} />
                        {downloading ? t("claims_invoices_downloading") : t("claims_invoices_download")}
                    </button>
                )}
            </div>
            {loading ? (
                <div className="order-notes-empty">{t("loading")}</div>
            ) : existing.length === 0 ? (
                <div className="order-notes-empty">{t("claims_existing_empty")}</div>
            ) : (
                <div className="locations-table-wrap order-claims-existing">
                    <table className="locations-table">
                        <thead>
                            <tr>
                                <th>{t("claims_col_parcel")}</th>
                                <th>{t("claims_col_sku")}</th>
                                <th>{t("qty")}</th>
                                <th>{t("claims_col_type")}</th>
                                <th>{t("claims_col_cost")}</th>
                                <th>{t("claims_col_status")}</th>
                                <th>{t("claims_col_by")}</th>
                                <th aria-label={t("claims_col_actions")} />
                            </tr>
                        </thead>
                        <tbody>
                            {existing.map(c => (
                                <tr key={c.claim_id}>
                                    <td className="dispatch-replacement-tracking">{c.tracking_number}</td>
                                    <td>{c.sku}</td>
                                    <td>{c.quantity}</td>
                                    <td>{t(TYPE_KEY[c.claim_type] ?? c.claim_type)}</td>
                                    <td>
                                        {money(Number(c.cost) || 0)}
                                        <span
                                            className={`order-claims-cost-source order-claims-cost-source--${c.cost_source ?? "portal"}`}
                                            title={c.cost_source === "invoice"
                                                ? t("claims_cost_source_invoice_title", { invoice: c.invoice_number ?? "" })
                                                : t("claims_cost_source_portal_title")}
                                        >
                                            {c.cost_source === "invoice" ? t("claims_cost_source_invoice") : t("claims_cost_source_portal")}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`order-claims-status order-claims-status--${c.status}`}>
                                            {c.status === "submitted"
                                                ? t("claims_status_submitted", { date: formatTs(c.submitted_at) })
                                                : t("claims_status_pending")}
                                        </span>
                                    </td>
                                    <td className="order-claims-by">{c.created_by_name || "—"} · {formatTs(c.created_at)}</td>
                                    <td>
                                        <button
                                            type="button"
                                            className="order-claims-invoices order-claims-invoices--row"
                                            disabled={downloading}
                                            title={t("claims_invoices_download")}
                                            aria-label={t("claims_invoices_row_aria", { sku: c.sku })}
                                            onClick={() => void downloadInvoices([c.claim_id])}
                                        >
                                            <FileText size={12} />
                                        </button>
                                        {c.status === "pending" && (
                                            <button
                                                type="button"
                                                className="order-claims-cancel"
                                                disabled={cancelling !== null || readOnly}
                                                aria-disabled={readOnly}
                                                title={readOnly ? t("read_only_mode") : undefined}
                                                aria-label={t("claims_cancel_aria", { sku: c.sku })}
                                                onClick={() => void cancelClaim(c)}
                                            >
                                                <X size={12} />
                                                {t("claims_cancel")}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default OrderClaims;
